import React, { lazy, Suspense, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from 'antd'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { useAssistantTasks } from '../hooks/useAssistantTasks'
import { useTaskActions } from '../hooks/useTaskActions'
import { ProjectGroup } from './ProjectGroup'
// Lazy: pulls antd DatePicker (~240 KB raw rc-picker). Modal-only widget,
// only mounted after the user clicks "+ Add task".
const ManualTaskForm = lazy(() =>
  import('./ManualTaskForm').then((m) => ({ default: m.ManualTaskForm })),
)
import { AssistantBuddy } from './AssistantBuddy'
import { groupByProject } from '../utils/group-by-project'
import { FLORIA_HEADER_GRADIENT } from '../styles/tokens'
import type { Task, TaskBucket } from '../schemas/task'

// One of the three filter tabs in the panel header. The 'waiting' tab
// represents the merged set of tasks whose derived bucket is `later` OR
// `waiting` — same merge as `summary.waiting` (parent FR-045).
type ActiveTab = 'urgent' | 'week' | 'waiting'

const bucketMatchesTab = (bucket: TaskBucket, tab: ActiveTab): boolean => {
  if (tab === 'urgent') return bucket === 'urgent'
  if (tab === 'week') return bucket === 'week'
  return bucket === 'later' || bucket === 'waiting'
}

export type AssistantPanelProps = {
  onClose: () => void
}

const HEADER_GRADIENT = FLORIA_HEADER_GRADIENT

// Floria's speech-bubble copy. Counts come from `useAssistantTasks`'s summary
// (each value = number of distinct projects whose top open task lands in
// that bucket). Priority order: urgent first, then this-week, then waiting,
// then the warm "all-clear" line.
const getFloriaMessage = (s: {
  urgent: number
  week: number
  waiting: number
}): string => {
  if (s.urgent > 0) {
    return s.urgent === 1
      ? 'Un dossier urgent vous attend !'
      : `${s.urgent} dossiers urgents vous attendent !`
  }
  if (s.week > 0) {
    return s.week === 1
      ? 'Un dossier à traiter cette semaine.'
      : `${s.week} dossiers à traiter cette semaine.`
  }
  if (s.waiting > 0) {
    return s.waiting === 1
      ? 'Un dossier en attente.'
      : `${s.waiting} dossiers en attente.`
  }
  return 'Rien ne vous attend — bon travail.'
}

export const AssistantPanel: React.FC<AssistantPanelProps> = ({ onClose }) => {
  const navigate = useNavigate()
  const [manualOpen, setManualOpen] = useState(false)
  const [manualPrefill, setManualPrefill] = useState<
    Partial<Task> | undefined
  >(undefined)

  const { tasks, summary, isLoading } = useAssistantTasks()

  // Tab state: null = use the -based default (urgent → week →
  // waiting), set = the user has manually picked a tab and that pick
  // sticks regardless of count changes.
  const [userPickedTab, setUserPickedTab] = useState<ActiveTab | null>(null)
  const defaultTab: ActiveTab = useMemo(() => {
    if (summary.urgent > 0) return 'urgent'
    if (summary.week > 0) return 'week'
    if (summary.waiting > 0) return 'waiting'
    return 'urgent'
  }, [summary.urgent, summary.week, summary.waiting])
  const activeTab: ActiveTab = userPickedTab ?? defaultTab

  const openManualForm = (prefill?: Partial<Task>) => {
    setManualPrefill(prefill)
    setManualOpen(true)
  }

  const { runPrimary, reschedule, ignore, createManual, pendingId } =
    useTaskActions({ openManualForm })

  const filteredTasks = useMemo(
    () => tasks.filter((t) => bucketMatchesTab(t.bucket, activeTab)),
    [tasks, activeTab],
  )
  const projectGroups = useMemo(() => groupByProject(filteredTasks), [filteredTasks])

  const onOpenProject = (projectId: number | null) => {
    if (projectId != null) void navigate(`/projects/edit/${projectId}`)
  }

  // "Tout replanifier": bulk-reschedule target_time over every task in the
  // group. Per-task and bulk both use the same mutation now that snooze is
  // gone (FR-037, FR-043).
  const onReplanifierAll = async (
    group: { items: Task[] },
    chosenDate: Date,
  ) => {
    for (const t of group.items) {
      // eslint-disable-next-line no-await-in-loop -- sequential so the optimistic cache update stays ordered
      await reschedule(t, chosenDate)
    }
  }

  return (
    <aside
      data-cy="assistant--panel"
      style={{
        width: 380,
        flex: 'none',
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 10,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 24px)',
        overflow: 'hidden',
        boxShadow: '0 12px 32px rgba(15,42,74,0.18)',
      }}
    >
      {/* Scene — full-width landscape strip with the buddy centered like
          standing in a field of flowers. Close button overlays the top-right
          corner of the scene. No surrounding tile / border. When the panel
          is empty, a speech bubble emerges from above the buddy. */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          minHeight: 124,
          // paddingTop shifts the flex-centered buddy lower so it reads as
          // standing inside the field rather than sitting at the vertical
          // center. Each 2px of padding moves the buddy down 1px.
          paddingTop: 40,
          background: `url(/floria/flower-landscape.svg) center bottom / 100% auto no-repeat, ${HEADER_GRADIENT}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <AssistantBuddy size={88} title="Floria" />
        {!isLoading && (
          <div
            data-cy="assistant--floria-message"
            role="status"
            aria-live="polite"
            style={{
              position: 'absolute',
              top: 22,
              left: '50%',
              transform: 'translateX(-50%)',
              maxWidth: 280,
              padding: '5px 12px',
              borderRadius: 14,
              background: 'rgba(255,255,255,0.96)',
              color: 'rgba(0,0,0,0.72)',
              fontSize: 12.5,
              fontStyle: 'italic',
              lineHeight: 1.35,
              boxShadow: '0 2px 8px rgba(0,0,0,0.14)',
              textAlign: 'center',
              pointerEvents: 'none',
              zIndex: 2,
            }}
          >
            {getFloriaMessage(summary)}
            {/* Tail: rotated square at bottom-center, points down to the buddy */}
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                bottom: -5,
                left: '50%',
                transform: 'translateX(-50%) rotate(45deg)',
                width: 10,
                height: 10,
                background: 'rgba(255,255,255,0.96)',
                boxShadow: '2px 2px 4px rgba(0,0,0,0.06)',
              }}
            />
          </div>
        )}
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
          aria-label="Replier"
          data-cy="action--assistant_close"
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            color: 'rgba(0,0,0,0.6)',
            background: 'rgba(255,255,255,0.55)',
            borderRadius: '50%',
            zIndex: 3,
          }}
        />
      </div>

      {/* Content header — name + meta + inline summary on the white surface */}
      <div style={{ padding: '12px 16px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 14 }}>Floria</div>
          <div style={{ fontSize: 11.5, color: 'rgba(0,0,0,0.45)' }}>
            {summary.total} dossier{summary.total > 1 ? 's' : ''}
            {summary.newToday > 0 && ` · ${summary.newToday} nouveau${summary.newToday > 1 ? 'x' : ''} aujourd'hui`}
          </div>
        </div>

        {/* Tab bar — three filtering tabs replacing the prior inline
            summary chips. Active tab is underlined in the bucket's accent
            color; clicking switches the body filter. */}
        <div
          role="tablist"
          aria-label="Filtrer par priorité"
          style={{
            display: 'flex',
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            marginTop: 10,
          }}
        >
          {([
            { id: 'urgent',  label: 'Urgent',         count: summary.urgent,  color: '#cf1322' },
            { id: 'week',    label: 'Cette semaine',  count: summary.week,    color: '#c2410c' },
            { id: 'waiting', label: 'En attente',     count: summary.waiting, color: '#531dab' },
          ] as const).map((t) => {
            const isActive = activeTab === t.id
            return (
              <button
                key={t.id}
                role="tab"
                type="button"
                aria-selected={isActive}
                onClick={() => setUserPickedTab(t.id)}
                data-cy={`assistant--tab_${t.id}`}
                style={{
                  flex: 1,
                  padding: '6px 4px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: `2px solid ${isActive ? t.color : 'transparent'}`,
                  marginBottom: -1,
                  cursor: 'pointer',
                  fontSize: 13,
                  lineHeight: 1.3,
                  color: isActive ? t.color : 'rgba(0,0,0,0.55)',
                  fontWeight: isActive ? 600 : 500,
                }}
              >
                <span style={{ fontWeight: 700 }}>{t.count}</span> {t.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Body — project-grouped task list (empty body when no tasks) */}
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 0 100px' }}>
        {isLoading || tasks.length === 0 ? null : (
          projectGroups.map((g, idx) => (
            <ProjectGroup
              key={g.key}
              group={g}
              defaultOpen={idx < 3}
              pendingId={pendingId}
              onPrimary={runPrimary}
              onReplanifier={reschedule}
              onIgnore={ignore}
              onOpenProject={onOpenProject}
              onReplanifierAll={onReplanifierAll}
            />
          ))
        )}

        <div style={{ padding: '12px 16px' }}>
          <Button
            type="dashed"
            block
            icon={<PlusOutlined />}
            onClick={() => openManualForm(undefined)}
            data-cy="action--assistant_create-manual"
          >
            Créer une tâche manuelle
          </Button>
        </div>
      </div>

      <Suspense fallback={null}>
      {manualOpen && (
      <ManualTaskForm
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onSubmit={createManual}
        initialValues={
          manualPrefill
            ? {
                title: manualPrefill.title,
                reason: manualPrefill.reason ?? undefined,
                meta: manualPrefill.meta ?? undefined,
                projectId: manualPrefill.projectId ?? null,
              }
            : undefined
        }
      />
      )}
      </Suspense>
    </aside>
  )
}
